/**
 * seo.js — Naturiq SEO helpers
 *
 * Usage:
 *   import { useSEO, buildProductSchema, buildBlogSchema, buildBreadcrumb } from '../lib/seo';
 *   useSEO({ title: 'Page Title', description: '...' });
 */

import { useEffect } from 'react';

const SITE_NAME   = 'Naturiq';
const SITE_URL    = 'https://naturiq.in';
const DEFAULT_IMG = `${SITE_URL}/Naturiq_logo.png`;
const DEFAULT_DESC =
  'Naturiq delivers premium indoor plants across India. Farm-fresh, quality-checked and survival-packed for your home or office.';
const TWITTER_HANDLE = '@naturiq_in';

// ─── Core hook ───────────────────────────────────────────────────────────────
/**
 * useSEO({ title, description, image, url, type, noindex, structuredData })
 *
 * Sets <title>, meta description, Open Graph, Twitter Card, canonical,
 * and optional JSON-LD structured data — all via direct DOM manipulation
 * so it works with react-scripts without needing HelmetProvider.
 */
export function useSEO({
  title,
  description = DEFAULT_DESC,
  image = DEFAULT_IMG,
  url,
  type = 'website',
  noindex = false,
  structuredData = null,
} = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Bring the forest home`;
    const canonicalUrl = url ? `${SITE_URL}${url}` : `${SITE_URL}${window.location.pathname}`;
    const absImage = image?.startsWith('http') ? image : `${SITE_URL}${image}`;

    // — Title
    document.title = fullTitle;

    // — Helper
    function setMeta(selector, attr, value) {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [attrName, attrVal] = selector.includes('property=')
          ? ['property', selector.match(/property="([^"]+)"/)[1]]
          : ['name', selector.match(/name="([^"]+)"/)[1]];
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    }

    function setLink(rel, href) {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    }

    // — Standard meta
    setMeta('meta[name="description"]',         'content', description);
    setMeta('meta[name="robots"]',              'content', noindex ? 'noindex,nofollow' : 'index,follow');

    // — Canonical
    setLink('canonical', canonicalUrl);

    // — Open Graph
    setMeta('meta[property="og:title"]',        'content', fullTitle);
    setMeta('meta[property="og:description"]',  'content', description);
    setMeta('meta[property="og:image"]',        'content', absImage);
    setMeta('meta[property="og:url"]',          'content', canonicalUrl);
    setMeta('meta[property="og:type"]',         'content', type);
    setMeta('meta[property="og:site_name"]',    'content', SITE_NAME);
    setMeta('meta[property="og:locale"]',       'content', 'en_IN');

    // — Twitter Card
    setMeta('meta[name="twitter:card"]',        'content', 'summary_large_image');
    setMeta('meta[name="twitter:site"]',        'content', TWITTER_HANDLE);
    setMeta('meta[name="twitter:title"]',       'content', fullTitle);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]',       'content', absImage);

    // — JSON-LD structured data
    const SCRIPT_ID = 'seo-structured-data';
    let script = document.getElementById(SCRIPT_ID);
    if (structuredData) {
      if (!script) {
        script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    } else if (script) {
      script.remove();
    }

    // Cleanup: reset to defaults on unmount
    return () => {
      document.title = `${SITE_NAME} — Bring the forest home`;
    };
  }, [title, description, image, url, type, noindex, structuredData]);
}

// ─── Structured-data builders ─────────────────────────────────────────────────

/** Product schema (Google Shopping, rich results) */
export function buildProductSchema(product, selectedPrice) {
  if (!product) return null;
  const price = selectedPrice || product.price || 0;
  const imageUrl =
    (product.images && product.images[0]?.url) ||
    product.imageUrl ||
    product.image ||
    DEFAULT_IMG;
  const absImage = imageUrl.startsWith('http') ? imageUrl : `${SITE_URL}${imageUrl}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description || DEFAULT_DESC,
    image: absImage,
    sku: product._id || product.id || product.slug,
    brand: { '@type': 'Brand', name: SITE_NAME },
    url: `${SITE_URL}/product/${product.slug}`,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'INR',
      price: price,
      availability: product.inStock === false
        ? 'https://schema.org/OutOfStock'
        : 'https://schema.org/InStock',
      seller: { '@type': 'Organization', name: SITE_NAME },
      url: `${SITE_URL}/product/${product.slug}`,
    },
    ...(product.rating
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: product.rating,
            reviewCount: product.reviews || product.reviewCount || 1,
            bestRating: 5,
            worstRating: 1,
          },
        }
      : {}),
  };
}

/** Blog/Article schema */
export function buildBlogSchema(post) {
  if (!post) return null;
  const absImage = post.cover?.startsWith('http') ? post.cover : `${SITE_URL}${post.cover || ''}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt || post.title,
    image: absImage || DEFAULT_IMG,
    datePublished: post.date || post.createdAt,
    dateModified: post.updatedAt || post.date || post.createdAt,
    author: {
      '@type': 'Person',
      name: post.author?.name || SITE_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: DEFAULT_IMG },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/blog/${post.slug}`,
    },
  };
}

/** BreadcrumbList schema */
export function buildBreadcrumb(items) {
  // items: [{ name, url }, ...]
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: item.name,
      item: `${SITE_URL}${item.url}`,
    })),
  };
}

/** Organization schema for homepage */
export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_IMG,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+91-76969-99018',
      contactType: 'customer service',
      areaServed: 'IN',
      availableLanguage: ['English', 'Hindi'],
    },
    sameAs: [
      'https://www.instagram.com/naturiq.in',
    ],
  };
}

/** FAQPage schema */
export function buildFAQSchema(faqs) {
  // faqs: [{ question, answer }, ...]
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };
}

/** WebSite schema with sitelinks searchbox */
export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/search?q={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
  };
}
